import Mustache from 'mustache';
import Questions from './components/Questions';
import { ArticleShow, ArticleSidebar, ArticlesService } from '@atreus/data';
import { Aside } from '../components/Aside';
import { Badges } from '../../../components/Badges';
import {
  Banner,
  HomeBanner
} from '../../homepage/components/interventions/HomeBanner';
import { Box, Container, Stack, Text, useMounted } from '@qcx/ui';
import { Breadcrumb, BreadcrumbLink } from '../../../components/Breadcrumb';
import { CommentsScrollButton } from './components/CommentsScrollButton';
import { LikeDislikeButtons } from './components/LikeDislikeButtons';
import { NewsStoreProvider } from '../news.store';
import { Page } from '../../../components/page/Page';
import { PageBelowTheFoldComponent } from './components/PageBelowTheFoldComponent';
import { SocialShareButtons } from '../components/SocialShareButtons';
import { Suspense, useEffect, useRef } from 'react';
import { bannerDefault } from '../../mocks/banner-default';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { data } from 'react-router';
import { generateCanonicalUrl } from '../../../lib/generate-canonical-url.lib';
import { getCommentaries } from './news.service';
import { getFirstWords } from '../../../lib/get-first-words.lib';
import { nexus } from '@kratos/nexus-client';
import { safeRenderHtml } from '../../../lib/safe-render-html.lib';
import { splitContentByParagraphs } from '../../../lib/split-content-by-paragrphs.lib';
import { transformHtml } from '../../../lib/transform-html.lib';
import type { Route } from './+types/index';

const getDefaultHref = (baseUrl: string): string => `${baseUrl}/feed`;

const getEditorialHref = (baseUrl: string, editorialSlug: string): string =>
  `${baseUrl}/${editorialSlug}/feed`;

const hasEditorials = (data: ArticleShow): boolean =>
  Boolean(data?.editorials?.length);

const extractAndTransformShortcodes = (content: string) => {
  const patterns: Record<string, RegExp> = {
    Q: /{{{Q\d+(?:, Q\d+)*(?:, [\dA-Za-z-]+)*}}}/g,
    HS: /{{{HS\d+}}}/g
  };

  const templates: Record<string, (value?: string) => string> = {
    Q: () => '{{{#question}}}{{{/question}}}',
    HS: (id?: string) => `{{{#hubspot}}}${id as string}{{{/hubspot}}}`
  };

  const matches: Record<string, string[]> = {
    Q: [],
    HS: []
  };

  for (const type in patterns) {
    const regex = patterns[type];
    const foundMatches = content.match(regex);

    if (foundMatches) {
      matches[type] = foundMatches;
    }
  }

  return content
    .replace(matches.Q[0], templates['Q']())
    .replace(
      matches.HS[0],
      templates['HS'](matches?.HS?.[0]?.replaceAll(/\D+/g, ''))
    );
};

const addingShortcodeContent = (content: string) => {
  return Mustache.render(
    extractAndTransformShortcodes(content),
    {
      // eslint-disable-next-line unicorn/consistent-function-scoping
      question: () => () =>
        '<div id="question" suppressHydrationWarning></div>',
      // eslint-disable-next-line unicorn/consistent-function-scoping
      hubspot: () => (id: string) => `
        <div class="hs-cta-embed hs-cta-embed-${id}" data-hubspot-wrapper-cta-id="${id}"></div>
        <img style="visibility:hidden;"
          src="https://no-cache.hubspot.com/cta/default/44326808/interactive-${id}.png"
          onerror="document.querySelector('.hs-cta-embed.hs-cta-embed-${id}').remove();this.remove()"
          onload="this.remove();" />
      `
    },
    {},
    ['{{{', '}}}']
  );
};

const getValidBanner = async () => {
  try {
    const bannerResponse = await nexus.getBanner({ id: 'atreus-home-primary' });
    const isBannerValid =
      bannerResponse.banner?.status === 'published' &&
      bannerResponse.banner?.smImage &&
      bannerResponse.banner?.mdImage &&
      bannerResponse.banner?.lgImage;

    if (isBannerValid) {
      return bannerResponse.banner;
    }
  } catch {
    return bannerDefault;
  }
};

export const meta = ({ data }: Route.MetaArgs) => {
  if (!data) {
    return [];
  }

  const baseUrl = data.baseUrl;
  const canonicalUrl = generateCanonicalUrl(data.url);
  const links = [];

  if (hasEditorials(data)) {
    for (const editorial of data.editorials) {
      links.push({
        tagName: 'link',
        rel: 'alternate',
        type: 'application/rss+xml',
        href: getEditorialHref(baseUrl, editorial.slug)
      });
    }
  } else {
    links.push({
      tagName: 'link',
      rel: 'alternate',
      type: 'application/rss+xml',
      href: getDefaultHref(baseUrl)
    });
  }

  return [
    { title: `${data.article.homeTitle} | Folha Dirigida` },
    { name: 'robots', content: 'max-image-preview:large' },
    { name: 'description', content: data.article.metadataConfig },
    { name: 'url', content: data.url },
    { name: 'name', content: data.article.title },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    {
      property: 'og:title',
      content: data.article.title
    },
    {
      property: 'og:type',
      content: 'website'
    },
    {
      property: 'og:description',
      content: data.article.description
    },
    {
      property: 'og:image',
      content: `${data.article.image}`
    },
    {
      property: 'og:image:width',
      content: '1200'
    },
    {
      property: 'og:image:height',
      content: '1200'
    },
    {
      property: 'og:url',
      content: data.url
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image'
    },
    {
      name: 'twitter:site',
      content: '@folhadirigida'
    },
    {
      name: 'twitter:creator',
      content: `${data.article.author}`
    },
    {
      name: 'twitter:url',
      content: data.url
    },
    {
      name: 'twitter:title',
      content: data.article.title
    },
    {
      name: 'twitter:description',
      content: data.article.description
    },
    {
      name: 'twitter:image',
      content: `${data.article.image}`
    },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Início',
            item: data.baseUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Editoria',
            item: `${data.baseUrl}/n`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: getFirstWords(data.article.title, 2)
          }
        ]
      }
    },
    ...links
  ];
};

export const loader = async ({
  params,
  request,
  context
}: Route.LoaderArgs) => {
  const url = new URL(request.url);
  const slug = params.slug as string;
  const articlesService = new ArticlesService();
  const currentUser = context.currentUser;

  const loadAllResources = async () => {
    const articlePromise = articlesService.getAtreusArticle({
      articleSlug: slug
    });

    const sidebarPromise = articlesService
      .getAtreusV2ArticlesSidebar({
        articleSlug: slug
      })
      .catch(() => ({}) as Record<string, unknown>);

    const article = await articlePromise;

    const commentariesPromise = currentUser
      ? getCommentaries(article.article.id, 1)
      : Promise.resolve({ commentaries: [], meta: { hasMore: false } });

    const contentProcessingPromise = new Promise<{
      firstPart: string;
      secondPart: string;
    }>(resolve => {
      const { firstPart, secondPart } = splitContentByParagraphs(
        article.article.content,
        3
      );

      resolve({
        firstPart: safeRenderHtml(
          addingShortcodeContent(transformHtml(firstPart))
        ),
        secondPart: safeRenderHtml(
          addingShortcodeContent(transformHtml(secondPart))
        )
      });
    });

    const [sidebar, getCommentariesResponse, processedContent] =
      await Promise.all([
        sidebarPromise,
        commentariesPromise,
        contentProcessingPromise
      ]);

    return { article, sidebar, getCommentariesResponse, processedContent };
  };

  const { article, sidebar, getCommentariesResponse, processedContent } =
    await loadAllResources();

  const baseContentGroup = 'fd news';
  const contentGroup = article.article.aiCreated
    ? `${baseContentGroup} ai`
    : `${baseContentGroup} human`;

  const bannerHighlight = await getValidBanner();

  return data({
    url: url.toString(),
    baseUrl: url.origin,
    ...article,
    sidebar,
    firstPart: processedContent.firstPart,
    secondPart: processedContent.secondPart,
    contentGroup,
    getCommentariesResponse,
    currentUser,
    articleId: article.article.id,
    userAuthenticated: !!currentUser,
    userInfo: {
      id: currentUser?.id,
      name: currentUser?.name,
      avatarUrl: currentUser?.avatarUrl
    },
    bannerHighlight: bannerHighlight as Banner
  });
};

export default function NewsShowPage({ loaderData }: Route.ComponentProps) {
  const {
    url,
    article,
    mostAccessed,
    articles,
    relatedNews,
    editorials,
    mostRecent,
    sidebar,
    firstPart,
    secondPart,
    banner,
    contentGroup,
    getCommentariesResponse,
    articleId,
    userAuthenticated,
    userInfo,
    bannerHighlight
  } = loaderData;

  useMounted(() => {
    if (window?.zaraz) {
      window.zaraz.set('content_group', contentGroup);
    }
  });

  const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const lastVNodeRef = useRef<React.ReactNode>(null);

  useMounted(() => {
    const selectEl = () =>
      document.querySelector('#question') as HTMLElement | null;

    const ensureRoot = () => {
      const el = selectEl();

      if (!el) {
        return;
      }

      if (!rootRef.current || containerRef.current !== el) {
        rootRef.current?.unmount();

        containerRef.current = el;

        const hasSSR = el.childNodes.length > 0;
        rootRef.current = hasSSR
          ? hydrateRoot(el, lastVNodeRef.current ?? null, {
              identifierPrefix: 'question',
              onRecoverableError(e, info) {
                // eslint-disable-next-line no-console
                console.error('SSR recoverable:', e, info);
              }
            })
          : createRoot(el, {
              identifierPrefix: 'question',
              onRecoverableError(e, info) {
                // eslint-disable-next-line no-console
                console.error('CSR recoverable:', e, info);
              }
            });

        if (!hasSSR && lastVNodeRef.current && 'render' in rootRef.current) {
          rootRef.current.render(lastVNodeRef.current);
        }
      }
    };

    ensureRoot();

    const obs = new MutationObserver(() => {
      const el = selectEl();

      if (el && el !== containerRef.current) {
        ensureRoot();
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });

    if (document.readyState === 'loading') {
      const onReady = () => {
        ensureRoot();
        document.removeEventListener('DOMContentLoaded', onReady);
      };
      document.addEventListener('DOMContentLoaded', onReady);
    }

    return () => {
      obs.disconnect();
    };
  });

  useEffect(() => {
    const el = document.querySelector('#question');

    if (!el || !article?.widget) {
      return;
    }

    try {
      const data = JSON.parse(article.widget);
      const ok = !!data?.courses?.[0] && (data?.questions?.length ?? 0) > 0;

      if (!ok) {
        return;
      }

      const vnode = (
        <Suspense fallback={<div>Carregando...</div>}>
          <Questions
            course={data.courses[0]}
            questions={data.questions}
            share={{ url, title: article.title }}
          />
        </Suspense>
      );
      lastVNodeRef.current = vnode;

      if (rootRef.current && 'render' in rootRef.current) {
        rootRef.current.render(vnode);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao renderizar Questions:', error);
    }
  }, [article.widget, article.title, url]);

  useMounted(() => {
    return () => {
      rootRef.current?.unmount();
      rootRef.current = null;
      containerRef.current = null;
      lastVNodeRef.current = null;
    };
  });

  const hasSidebar = Boolean(sidebar && Object.values(sidebar).length > 0);

  return (
    <NewsStoreProvider
      initialState={{
        isLikeActive: false,
        isDislikeActive: false,
        likes: article.likes,
        dislikes: article.dislikes,
        comments: {
          ...getCommentariesResponse
        },
        userInfo: userAuthenticated ? userInfo : undefined,
        articleId
      }}
    >
      <Page>
        <Container
          css={{
            pb: '$4',
            '@lg': {
              pb: '$2'
            }
          }}
        >
          <Box css={{ mt: '$4' }}>
            <HomeBanner
              id={bannerHighlight.id}
              alt={bannerHighlight.alt}
              isHome={false}
              lgImage={bannerHighlight.lgImage}
              link={bannerHighlight.link}
              mdImage={bannerHighlight.mdImage}
              smImage={bannerHighlight.smImage}
              title={bannerHighlight.title}
            />
          </Box>
          <Stack
            css={{
              flexColCenter: !hasSidebar
            }}
            flow={{
              '@initial': 'column',
              '@lg': 'row'
            }}
            gap="$8"
          >
            <Box>
              <Box css={{ maxW: 720 }}>
                <Box
                  css={{
                    mb: '$2'
                  }}
                >
                  <Breadcrumb>
                    <BreadcrumbLink href={'/'}>Início</BreadcrumbLink>
                    <BreadcrumbLink href={'/noticias-de-concursos'}>
                      Notícias de Concursos
                    </BreadcrumbLink>
                    <BreadcrumbLink isCurrentPage>
                      {article.title}
                    </BreadcrumbLink>
                  </Breadcrumb>
                </Box>
                <Box as="header">
                  <Text
                    as="h1"
                    dangerouslySetInnerHTML={{
                      __html: safeRenderHtml(article.title)
                    }}
                    variant={{
                      '@initial': 'displayXs',
                      '@lg': 'displaySm'
                    }}
                    weight="semibold"
                  />
                  {article.description && (
                    <Text
                      as="h2"
                      css={{ color: '$textMuted', my: '$4' }}
                      dangerouslySetInnerHTML={{
                        __html: safeRenderHtml(article.description)
                      }}
                      variant={{
                        '@initial': 'md',
                        '@lg': 'lg'
                      }}
                    />
                  )}
                  {article.editorials.length > 0 && (
                    <Badges items={article.editorials} />
                  )}
                  <Stack
                    css={{
                      pt: '$6',
                      gap: '$2',
                      textSm: true,
                      '@lg': {
                        gap: '$4',
                        textMd: true
                      }
                    }}
                    flow={{
                      '@initial': 'column',
                      '@sm': 'row'
                    }}
                  >
                    <Stack css={{ noWrap: true }} gap="$2">
                      <Text css={{ fontWeight: '$semibold' }}>Autor:</Text>
                      <Text>{article.author}</Text>
                    </Stack>
                    <Stack css={{ noWrap: true }} gap="$2">
                      <Text css={{ fontWeight: '$semibold' }}>
                        Publicado em:
                      </Text>
                      <Text>{article.publishedAt}</Text>
                    </Stack>
                    <Stack css={{ noWrap: true, gap: '$2' }}>
                      <Text css={{ fontWeight: '$semibold' }}>
                        Atualizado em:
                      </Text>
                      <Text>{article.updatedAt}</Text>
                    </Stack>
                  </Stack>
                  <Stack
                    css={{
                      my: '$16',
                      flexDirection: 'column',
                      gap: '$4',
                      '@lg': { flexDirection: 'row', gap: '$12' }
                    }}
                  >
                    <Stack css={{ gap: '$4' }}>
                      <LikeDislikeButtons
                        slug={article?.slug}
                        dislikeDisabled
                      />
                      <CommentsScrollButton />
                    </Stack>
                    <Box css={{ borderRight: '1px solid $separatorColor' }} />
                    <SocialShareButtons url={url} />
                  </Stack>
                </Box>
                <PageBelowTheFoldComponent
                  article={article}
                  articles={articles}
                  banner={banner}
                  editorials={editorials}
                  firstPart={firstPart}
                  mostAccessed={mostAccessed}
                  mostRecent={mostRecent}
                  relatedNews={relatedNews}
                  secondPart={secondPart}
                />
              </Box>
            </Box>
            {hasSidebar && <Aside data={sidebar as ArticleSidebar} />}
          </Stack>
        </Container>
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'NewsArticle',
              headline: article.title,
              image: [article.image],
              datePublished: article.publishedAtIso,
              dateModified: article.updatedAtIso,
              author: article.author
            })
          }}
          type="application/ld+json"
        />
      </Page>
    </NewsStoreProvider>
  );
}
